import { useCallback, useRef, useState } from 'react';

type Options = {
  /** The element the drag is measured against - the track, or the waveform. */
  trackRef: React.RefObject<HTMLElement | null>;
  /** Total length of the media, in seconds. Zero disables seeking. */
  duration: number;
  /** Called with the seconds the pointer landed on. */
  onSeek: (seconds: number) => void;
};

/**
 * Drag-to-seek for a media scrubber.
 *
 * Both players draw their own track - a bar for video, the waveform for a voice
 * note - and both need the same four pointer handlers: press to jump, drag to
 * scrub, release to stop. Pointer capture is what keeps the drag alive once the
 * cursor leaves the (4px tall) track, which a plain mousemove listener would
 * not.
 *
 * Spread `seekHandlers` on the track and give it `touch-action: none`, or the
 * browser's scroll gesture wins on touch.
 */
export const usePointerSeek = ({ trackRef, duration, onSeek }: Options) => {
  const [seeking, setSeeking] = useState(false);
  // Read inside the handlers rather than closed over, so a drag that started
  // before the metadata arrived still seeks against the real duration.
  const seekingRef = useRef(false);

  const seekToClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || !duration) return;
      const rect = track.getBoundingClientRect();
      const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
      onSeek((x / rect.width) * duration);
    },
    [trackRef, duration, onSeek]
  );

  const setBoth = (value: boolean) => {
    seekingRef.current = value;
    setSeeking(value);
  };

  const seekHandlers = {
    onPointerDown: (event: React.PointerEvent) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      setBoth(true);
      seekToClientX(event.clientX);
    },
    onPointerMove: (event: React.PointerEvent) => {
      if (seekingRef.current) seekToClientX(event.clientX);
    },
    onPointerUp: (event: React.PointerEvent) => {
      event.currentTarget.releasePointerCapture(event.pointerId);
      setBoth(false);
    },
    onPointerCancel: () => setBoth(false),
  };

  return { seeking, seekHandlers };
};
